// src/components/MinecraftApplicationPage.jsx
//
// Standalone whitelist application page for TOA RP. Deliberately separate
// from MinecraftPage.jsx — its own header, its own layout, its own visual
// language — but still lives at minecraft.theonesabove.com/apply.
import { useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowLeft, User, BookOpen, ShieldCheck, MessagesSquare, CheckCircle2,
  ChevronRight, ChevronLeft, Send, AlertCircle, Mic, Compass, Save,
} from "lucide-react";
import { API } from "../utils/constants";
import { useAuth } from "../context/AuthContext";

/* ─────────────────────────────────────────────────────────────────────────
   Config
   ───────────────────────────────────────────────────────────────────────── */
const STEPS = [
  { id: "player",    label: "About You",   short: "You",      Icon: User },
  { id: "character", label: "Character",   short: "Character", Icon: BookOpen },
  { id: "knowledge", label: "RP Knowledge", short: "Knowledge", Icon: ShieldCheck },
  { id: "scenarios", label: "Scenarios",   short: "Scenarios", Icon: MessagesSquare },
  { id: "review",    label: "Review & Submit", short: "Review", Icon: CheckCircle2 },
];

const EMPTY_FORM = {
  minecraftUsername: "", discordUsername: "", dateOfBirth: "", timezone: "", microphone: "yes",
  weeklyAvailability: "", referral: "", rpExperience: "", previousServers: "",
  characterName: "", characterAge: "", characterOrigin: "", characterOccupation: "",
  characterBackstory: "", characterPersonality: "", characterGoals: "", characterFlaws: "",
  metagaming: "", powergaming: "", failRp: "", valueOfLife: "",
  scenarioConflict: "", scenarioInformation: "", scenarioRobbery: "", scenarioRulebreak: "",
  whyTOA: "", rulesAccepted: false, truthfulAccepted: false,
};

const STEP_FIELDS = {
  0: ["minecraftUsername", "discordUsername", "dateOfBirth", "timezone", "weeklyAvailability", "rpExperience"],
  1: ["characterName", "characterAge", "characterOrigin", "characterBackstory", "characterPersonality", "characterGoals", "characterFlaws"],
  2: ["metagaming", "powergaming", "failRp", "valueOfLife"],
  3: ["scenarioConflict", "scenarioInformation", "scenarioRobbery", "scenarioRulebreak", "whyTOA"],
};

const DRAFT_KEY = "toa_mc_rp_application_draft_v1";

function loadDraft() {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...EMPTY_FORM, ...parsed };
  } catch {
    return null;
  }
}

function saveDraft(form) {
  try { window.localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
}

function clearDraft() {
  try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

/* ─────────────────────────────────────────────────────────────────────────
   Small building blocks
   ───────────────────────────────────────────────────────────────────────── */
function Field({ label, required, hint, prompt, error, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8,
        marginBottom: 8,
      }}>
        <span style={{
          fontFamily: "'Cinzel',serif", fontSize: 11, letterSpacing: 1.1,
          color: "#cfe6d6", textTransform: "uppercase",
        }}>
          {label}{required && <span style={{ color: "#4ade80", marginLeft: 4 }}>*</span>}
        </span>
      </div>
      {prompt && (
        <div style={{
          fontSize: 13, color: "rgba(226,238,229,.9)", marginBottom: 10, lineHeight: 1.65,
        }}>
          {prompt}
        </div>
      )}
      {children}
      {error
        ? <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#f87171", marginTop: 6 }}>
            <AlertCircle style={{ width: 12, height: 12, flexShrink: 0 }} />Please complete this field.
          </div>
        : hint && <div style={{ fontSize: 11.5, color: "rgba(178,196,184,.55)", marginTop: 6, lineHeight: 1.55 }}>{hint}</div>}
    </label>
  );
}

const baseFieldStyle = {
  width: "100%", boxSizing: "border-box",
  background: "rgba(255,255,255,.03)", color: "#eef7f0",
  border: "1px solid rgba(255,255,255,.1)", borderRadius: 10,
  padding: "12px 14px", fontFamily: "'Inter',sans-serif", fontSize: 13.5,
  outline: "none", transition: "border-color .15s, box-shadow .15s, background .15s",
};

function useFieldProps(form, setField, errors) {
  return useCallback((key, extra = {}) => ({
    ...baseFieldStyle,
    ...extra,
    value: form[key],
    onChange: (e) => setField(key, e.target.value),
    style: {
      ...baseFieldStyle,
      ...extra.style,
      borderColor: errors[key] ? "rgba(248,113,113,.55)" : "rgba(255,255,255,.1)",
    },
    onFocus: (e) => {
      if (errors[key]) return;
      e.currentTarget.style.borderColor = "rgba(74,222,128,.55)";
      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(74,222,128,.08)";
      e.currentTarget.style.background = "rgba(74,222,128,.03)";
    },
    onBlur: (e) => {
      e.currentTarget.style.borderColor = errors[key] ? "rgba(248,113,113,.55)" : "rgba(255,255,255,.1)";
      e.currentTarget.style.boxShadow = "none";
      e.currentTarget.style.background = "rgba(255,255,255,.03)";
    },
  }), [form, setField, errors]);
}

/* ─────────────────────────────────────────────────────────────────────────
   Progress
   ───────────────────────────────────────────────────────────────────────── */
function StepProgress({ step }) {
  const pct = (step / (STEPS.length - 1)) * 100;
  return (
    <div style={{ marginBottom: 30 }}>
      <div className="mcap-step-row" style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        {STEPS.map((s, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, flex: i === STEPS.length - 1 ? "0 0 auto" : 1 }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                display: "grid", placeItems: "center",
                border: `1px solid ${active || done ? "rgba(74,222,128,.6)" : "rgba(255,255,255,.14)"}`,
                background: done ? "#4ade80" : active ? "rgba(74,222,128,.14)" : "rgba(255,255,255,.02)",
                color: done ? "#06210f" : active ? "#4ade80" : "rgba(255,255,255,.35)",
                transition: "all .25s",
              }}>
                {done ? <CheckCircle2 style={{ width: 15, height: 15 }} /> : <s.Icon style={{ width: 14, height: 14 }} />}
              </div>
              <span className="mcap-step-label" style={{
                fontFamily: "'Cinzel',serif", fontSize: 10.5, letterSpacing: .6, textTransform: "uppercase",
                color: active ? "#e8f5ec" : done ? "rgba(232,245,236,.6)" : "rgba(232,245,236,.32)",
                whiteSpace: "nowrap",
              }}>{s.short}</span>
              {i < STEPS.length - 1 && (
                <div style={{ flex: 1, height: 1, minWidth: 16, background: done ? "rgba(74,222,128,.5)" : "rgba(255,255,255,.1)", transition: "background .25s" }} />
              )}
            </div>
          );
        })}
      </div>
      <div style={{ height: 3, borderRadius: 3, background: "rgba(255,255,255,.06)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#2f9e57,#4ade80)", borderRadius: 3, transition: "width .35s cubic-bezier(.16,1,.3,1)" }} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────────────────── */
export default function MinecraftApplicationPage({ onBack, onOpenAuth }) {
  const { authUser, accessToken, loading: authLoading } = useAuth();
  const [myApplication, setMyApplication] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    document.title = "Apply — TOA RP";
    const draft = loadDraft();
    if (draft && Object.values(draft).some((v) => typeof v === "string" && v.trim())) {
      setForm(draft);
      setDraftRestored(true);
    }
    return () => { document.title = "The Ones Above"; };
  }, []);

  useEffect(() => {
    if (submitState === "success") return;
    const t = setTimeout(() => saveDraft(form), 400);
    return () => clearTimeout(t);
  }, [form, submitState]);

  useEffect(() => {
    if (!authUser || !accessToken) {
      setMyApplication(null);
      return;
    }
    let cancelled = false;
    setApplicationLoading(true);
    fetch(`${API}/minecraft/rp-applications/mine`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("Could not load application status");
        return r.json();
      })
      .then((d) => { if (!cancelled) setMyApplication(d.application || null); })
      .catch(() => { if (!cancelled) setMyApplication(null); })
      .finally(() => { if (!cancelled) setApplicationLoading(false); });
    return () => { cancelled = true; };
  }, [authUser, accessToken, submitState]);

  useEffect(() => {
    if (authUser?.minecraft_username && !form.minecraftUsername) {
      setForm((prev) => ({ ...prev, minecraftUsername: authUser.minecraft_username }));
    }
  }, [authUser, form.minecraftUsername]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
  }, []);

  const input = useFieldProps(form, setField, errors);

  const scrollTop = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validateStep = (idx) => {
    const next = {};
    (STEP_FIELDS[idx] || []).forEach((k) => {
      if (!String(form[k] ?? "").trim()) next[k] = true;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) { scrollTop(); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    scrollTop();
  };
  const goBackStep = () => {
    setErrors({});
    setStep((s) => Math.max(0, s - 1));
    scrollTop();
  };

  const submit = async () => {
    if (!form.rulesAccepted || !form.truthfulAccepted) {
      setErrors({ agreements: true });
      scrollTop();
      return;
    }
    setSubmitting(true);
    setSubmitState(null);
    try {
      const res = await fetch(`${API}/minecraft/rp-applications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Application service unavailable");
      setMyApplication(data.application || null);
      setSubmitState("success");
      clearDraft();
      scrollTop();
    } catch (e) {
      setSubmitState("unavailable");
      scrollTop();
    } finally {
      setSubmitting(false);
    }
  };

  const textArea = (key, placeholder, minHeight = 120) => (
    <textarea {...input(key, { style: { minHeight, resize: "vertical", lineHeight: 1.6 } })} placeholder={placeholder} />
  );

  const twoCol = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 };
  const stack = { display: "grid", gap: 20 };

  return (
    <div style={{ minHeight: "100vh", position: "relative", overflow: "hidden" }} ref={topRef}>
      {/* Background */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", background: "linear-gradient(165deg,#081409 0%,#050c08 45%,#03080a 100%)" }} />
      <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}>
        <div style={{ position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)", width: 900, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(74,222,128,.09) 0%,transparent 70%)", filter: "blur(60px)" }} />
        <div style={{ position: "absolute", bottom: "-10%", right: "-10%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(155,127,212,.06) 0%,transparent 70%)", filter: "blur(60px)" }} />
      </div>

      <style>{`
        @keyframes mcapFadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .mcap-panel{animation:mcapFadeUp .35s cubic-bezier(.16,1,.3,1)}
        .mcap-two{display:grid;grid-template-columns:1fr 1fr;gap:18px}
        .mcap-field textarea, .mcap-field input, .mcap-field select{font-size:13.5px}
        .mcap-select{
          appearance:none;
          -webkit-appearance:none;
          cursor:pointer;
          padding-right:42px!important;
          background-color:rgba(255,255,255,.035)!important;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2386efac' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")!important;
          background-repeat:no-repeat!important;
          background-position:right 14px center!important;
          background-size:14px!important;
        }
        .mcap-select option{
          background:#081409;
          color:#eef7f0;
          padding:10px;
        }
        .mcap-field input[type='date']{color-scheme:dark}
        .mcap-field input[type='date']::-webkit-calendar-picker-indicator{
          filter:invert(82%) sepia(31%) saturate(706%) hue-rotate(79deg) brightness(101%) contrast(88%);
          cursor:pointer;
        }
        @media(max-width:720px){
          .mcap-two{grid-template-columns:1fr!important}
          .mcap-step-label{display:none}
          .mcap-card{padding:20px 16px!important}
          .mcap-hero-title{font-size:26px!important}
        }
      `}</style>

      {/* Minimal header — deliberately not the Minecraft tab nav */}
      <header style={{
        position: "sticky", top: 0, zIndex: 20,
        display: "flex", alignItems: "center",
        padding: "16px 28px", background: "rgba(5,12,8,.85)", backdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(74,222,128,.12)",
      }}>
        <a href="https://theonesabove.com/" className="toa-logo" style={{ textDecoration: "none" }}>
          The Ones Above
        </a>
      </header>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "18px 20px 80px", position: "relative", zIndex: 1 }}>
        <button
          onClick={onBack}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, background: "transparent",
            border: "none", padding: "7px 0", marginBottom: 18,
            color: "rgba(207,230,214,.75)", cursor: "pointer", fontFamily: "'Cinzel',serif", fontSize: 10.5,
            letterSpacing: .8, textTransform: "uppercase", transition: ".15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#4ade80"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(207,230,214,.75)"; }}
        >
          <ArrowLeft style={{ width: 13, height: 13 }} />Back
        </button>

        {authLoading ? (
          <div className="mcap-panel" style={{ padding: "38px 28px", textAlign: "center", borderRadius: 16, background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.08)", color: "rgba(217,227,220,.7)" }}>
            Checking your account…
          </div>
        ) : !authUser ? (
          <div className="mcap-panel" style={{ padding: "42px 28px", textAlign: "center", borderRadius: 16, background: "rgba(255,255,255,.025)", border: "1px solid rgba(74,222,128,.18)" }}>
            <ShieldCheck style={{ width: 34, height: 34, color: "#4ade80", marginBottom: 14 }} />
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 24, color: "#fff", marginBottom: 10 }}>Sign in to apply</div>
            <div style={{ fontSize: 13, color: "rgba(217,227,220,.7)", lineHeight: 1.7, maxWidth: 520, margin: "0 auto 22px" }}>
              A TOA account is required before you can submit a whitelist application. This links the application to you so you can return here and track its status.
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
              <button className="toa-btn toa-btn-p" onClick={() => onOpenAuth?.("login")}>Log In</button>
              <button className="toa-btn toa-btn-ghost" onClick={() => onOpenAuth?.("signup")}>Register</button>
            </div>
          </div>
        ) : applicationLoading ? (
          <div className="mcap-panel" style={{ padding: "38px 28px", textAlign: "center", borderRadius: 16, background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.08)", color: "rgba(217,227,220,.7)" }}>
            Loading your application…
          </div>
        ) : myApplication && myApplication.status !== "rejected" && submitState !== "success" ? (
          <div className="mcap-panel" style={{ padding: "46px 28px", textAlign: "center", borderRadius: 18, background: "rgba(255,255,255,.025)", border: "1px solid rgba(74,222,128,.22)" }}>
            <CheckCircle2 style={{ width: 38, height: 38, color: myApplication.status === "approved" ? "#4ade80" : "#f0d878", marginBottom: 14 }} />
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 26, color: "#fff", marginBottom: 9 }}>Your Application</div>
            <div style={{ fontSize: 12, color: "rgba(178,196,184,.6)", marginBottom: 8 }}>Application #{myApplication.id}</div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase", color: myApplication.status === "approved" ? "#4ade80" : "#f0d878", marginBottom: 14 }}>{myApplication.status}</div>
            <div style={{ fontSize: 13, color: "rgba(217,227,220,.72)", lineHeight: 1.7, maxWidth: 520, margin: "0 auto" }}>
              {myApplication.status === "approved" ? "Your whitelist application has been approved." : "Your application is currently waiting for staff review. Return to this page while signed in to check for updates."}
            </div>
          </div>
        ) : (
        submitState === "success" ? (
          <div className="mcap-panel" style={{
            textAlign: "center", padding: "64px 28px", borderRadius: 18,
            background: "rgba(255,255,255,.025)", border: "1px solid rgba(74,222,128,.22)",
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%", margin: "0 auto 22px",
              display: "grid", placeItems: "center",
              background: "rgba(74,222,128,.12)", border: "1px solid rgba(74,222,128,.3)",
              boxShadow: "0 0 40px rgba(74,222,128,.18)",
            }}>
              <CheckCircle2 style={{ width: 34, height: 34, color: "#4ade80" }} />
            </div>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 28, color: "#fff", marginBottom: 12 }}>
              Application Submitted
            </div>
            <div style={{ fontSize: 13.5, color: "rgba(217,227,220,.75)", lineHeight: 1.8, maxWidth: 480, margin: "0 auto 26px" }}>
              Your whitelist application has been received and linked to your TOA account. You can return to this page while signed in to track whether it is pending, approved or rejected.
            </div>
            <button className="toa-btn toa-btn-ghost" onClick={onBack}>Return to Minecraft</button>
          </div>
        ) : (
          <>
            {myApplication?.status === "rejected" && (
              <div style={{
                marginBottom: 20, padding: "14px 16px", borderRadius: 10,
                background: "rgba(248,113,113,.055)", border: "1px solid rgba(248,113,113,.2)",
                color: "rgba(254,202,202,.92)", fontSize: 12.5, lineHeight: 1.65, textAlign: "center",
              }}>
                <strong style={{ fontFamily: "'Cinzel',serif", letterSpacing: .5 }}>Previous application #{myApplication.id}: Rejected.</strong><br />
                You may submit a new application below. Review the rules and improve any areas of your previous application before reapplying.
              </div>
            )}
            <div className="mcap-panel" style={{ textAlign: "center", marginBottom: 36 }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 10.5, letterSpacing: 5, color: "#4ade80", textTransform: "uppercase", marginBottom: 8 }}>
                Whitelist Application
              </div>
              <div className="mcap-hero-title" style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: "clamp(26px,4.5vw,38px)", color: "#f4f7f4", marginBottom: 10 }}>
                Join the Roleplay World
              </div>
              <div style={{ fontSize: 13.5, color: "rgba(217,227,220,.65)", maxWidth: 520, margin: "0 auto", lineHeight: 1.7 }}>
                A few thoughtful answers go a long way — there are no trick questions. The Ones Above is a fantasy RP world with magic and custom systems, so staff are looking for consistent, cooperative roleplay rather than strict real-world realism.
              </div>
              <div style={{
                maxWidth: 620, margin: "18px auto 0", padding: "12px 14px", borderRadius: 10,
                display: "flex", alignItems: "flex-start", justifyContent: "center", gap: 9,
                background: "rgba(201,168,76,.055)", border: "1px solid rgba(201,168,76,.18)",
                color: "rgba(236,228,201,.85)", fontSize: 12, lineHeight: 1.65, textAlign: "left",
              }}>
                <BookOpen style={{ width: 14, height: 14, color: "#c9a84c", marginTop: 2, flexShrink: 0 }} />
                <span>
                  We highly suggest you read the <a href="https://minecraft.theonesabove.com/rules" style={{ color: "#f0d878", textDecoration: "underline", textUnderlineOffset: 3 }}>server rules</a> before proceeding with your application.
                </span>
              </div>
              {draftRestored && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 7, marginTop: 16,
                  padding: "6px 12px", borderRadius: 20, background: "rgba(74,222,128,.06)",
                  border: "1px solid rgba(74,222,128,.18)", color: "#86efac", fontSize: 11.5,
                }}>
                  <Save style={{ width: 12, height: 12 }} />Picked up where you left off
                </div>
              )}
            </div>

            <StepProgress step={step} />

            <div className="mcap-panel mcap-card" key={step} style={{
              padding: "28px 30px 30px", borderRadius: 16,
              background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.08)",
              boxShadow: "0 20px 60px rgba(0,0,0,.35)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
                {(() => { const Icon = STEPS[step].Icon; return <Icon style={{ width: 17, height: 17, color: "#4ade80" }} />; })()}
                <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, color: "#edf5ef" }}>{STEPS[step].label}</div>
              </div>

              <div className="mcap-field">
                {step === 0 && (
                  <div style={stack}>
                    <div className="mcap-two">
                      <Field label="Minecraft username" required error={errors.minecraftUsername}>
                        <input {...input("minecraftUsername")} placeholder="Your in-game name" />
                      </Field>
                      <Field label="Discord username" required error={errors.discordUsername}>
                        <input {...input("discordUsername")} placeholder="e.g. username" />
                      </Field>
                      <Field label="Date of birth" required error={errors.dateOfBirth} hint="Used only to help staff assess the application.">
                        <input {...input("dateOfBirth")} type="date" />
                      </Field>
                      <Field label="Timezone" required error={errors.timezone}>
                        <input {...input("timezone")} placeholder="e.g. GMT / BST" />
                      </Field>
                      <Field label="Working microphone">
                        <select {...input("microphone")} className="mcap-select">
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="sometimes">Sometimes / limited access</option>
                        </select>
                      </Field>
                      <Field label="How did you find TOA?">
                        <input {...input("referral")} placeholder="Discord, friend, YouTube, etc." />
                      </Field>
                    </div>
                    <Field label="Weekly availability" required error={errors.weeklyAvailability} hint="Roughly which days and times you tend to play.">
                      {textArea("weeklyAvailability", "Example: weekdays after 7pm, most weekends...", 90)}
                    </Field>
                    <Field label="Roleplay experience" required error={errors.rpExperience} hint="New to RP is completely fine — tell us what experience you do have.">
                      {textArea("rpExperience", "Previous roleplay games, servers, tabletop RP, or why you want to start...", 110)}
                    </Field>
                    <Field label="Previous RP servers">
                      <input {...input("previousServers")} placeholder="Optional — server names or communities" />
                    </Field>
                  </div>
                )}

                {step === 1 && (
                  <div style={stack}>
                    <div className="mcap-two">
                      <Field label="Character name" required error={errors.characterName}>
                        <input {...input("characterName")} placeholder="Full roleplay name" />
                      </Field>
                      <Field label="Character age" required error={errors.characterAge}>
                        <input {...input("characterAge")} type="number" min="1" placeholder="Character age" />
                      </Field>
                      <Field label="Place of origin" required error={errors.characterOrigin}>
                        <input {...input("characterOrigin")} placeholder="Where are they from?" />
                      </Field>
                      <Field label="Occupation / intended role">
                        <input {...input("characterOccupation")} placeholder="Trader, guard, farmer, criminal, etc." />
                      </Field>
                    </div>
                    <Field label="Backstory" required error={errors.characterBackstory} hint="Enough detail for staff to understand who this person is and what shaped them.">
                      {textArea("characterBackstory", "Their history, important events, relationships, and how they arrived in the world...", 150)}
                    </Field>
                    <Field label="Personality" required error={errors.characterPersonality}>
                      {textArea("characterPersonality", "How do they behave? What do they value? How do they treat strangers and rivals?", 100)}
                    </Field>
                    <div className="mcap-two">
                      <Field label="Goals & motivations" required error={errors.characterGoals}>
                        {textArea("characterGoals", "What does your character want to achieve?", 100)}
                      </Field>
                      <Field label="Flaws & weaknesses" required error={errors.characterFlaws} hint="Believable characters have limitations.">
                        {textArea("characterFlaws", "What gets them into trouble or holds them back?", 100)}
                      </Field>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div style={stack}>
                    <Field label="What is metagaming? Give an example." required error={errors.metagaming}>
                      {textArea("metagaming", "Explain it in your own words and give a simple RP example.", 110)}
                    </Field>
                    <Field label="What is powergaming? Give an example." required error={errors.powergaming}>
                      {textArea("powergaming", "What does it mean to force or unrealistically control an RP situation?", 110)}
                    </Field>
                    <Field label="What does FailRP mean on a fantasy RP server?" required error={errors.failRp}>
                      {textArea("failRp", "Explain how FailRP differs from something simply being unrealistic. Think about consistency with the server's lore, magic/mechanics, active scenes and consequences.", 110)}
                    </Field>
                    <Field label="What does valuing your character's life mean?" required error={errors.valueOfLife}>
                      {textArea("valueOfLife", "How should your character react when facing genuine danger or serious consequences?", 110)}
                    </Field>
                  </div>
                )}

                {step === 3 && (
                  <div style={stack}>
                    <Field
                      label="Scenario 1 — Faction turf conflict"
                      required
                      error={errors.scenarioConflict}
                      prompt="You are a member of Alnurayn and your group enters a contested turf outside the city that is currently controlled by the Knights Templars. A dispute develops and both sides are armed. How would you handle the situation while keeping the conflict believable, roleplay-focused, and within server rules?"
                    >
                      {textArea("scenarioConflict", "Explain what your character would do and how you would avoid turning the scene into a simple deathmatch.", 125)}
                    </Field>
                    <Field
                      label="Scenario 2 — Out-of-character information"
                      required
                      error={errors.scenarioInformation}
                      prompt="While reading Discord, you learn that members of the Rothschild faction plan to move a large amount of money through the city bank and that only two members will be present. Your character has not learned this information in-game. What do you do?"
                    >
                      {textArea("scenarioInformation", "Explain how you separate what you know as a player from what your character is allowed to know.", 125)}
                    </Field>
                    <Field
                      label="Scenario 3 — Robbery and City Guards"
                      required
                      error={errors.scenarioRobbery}
                      prompt="You leave the casino carrying a large amount of cash in your pocket instead of depositing it in the bank. Later, while passing through an area where robberies are permitted, two players stop you and threaten you for the money. You are outnumbered. How would you roleplay the situation, and what would you do afterwards if you wanted to involve the City Guards?"
                    >
                      {textArea("scenarioRobbery", "Describe how your character reacts to the threat, values their life, handles the cash at risk, and continues the RP afterwards.", 125)}
                    </Field>
                    <Field
                      label="Scenario 4 — Job roleplay and escalation"
                      required
                      error={errors.scenarioRulebreak}
                      prompt="You are working a shift at a coffee shop when a player starts a confrontation inside the shop. They become aggressive and the situation begins to escalate. City Guards and the City Emergency Service are available, and arrested players may be taken to prison. What would you do in-character, and what would you do separately if you believed a server rule had been broken?"
                    >
                      {textArea("scenarioRulebreak", "Explain how you would keep the shop scene in-character, respond to the escalation, involve city services where appropriate, and handle any rule report separately from the RP.", 125)}
                    </Field>
                    <Field label="Why do you want to join The Ones Above RP?" required error={errors.whyTOA}>
                      {textArea("whyTOA", "What interests you about this server specifically?", 100)}
                    </Field>
                  </div>
                )}

                {step === 4 && (
                  <div style={stack}>
                    <div style={{
                      padding: "14px 16px", borderRadius: 10, display: "flex", gap: 11, alignItems: "flex-start",
                      background: "rgba(74,222,128,.04)", border: "1px solid rgba(74,222,128,.16)",
                    }}>
                      <Compass style={{ width: 16, height: 16, color: "#4ade80", marginTop: 1, flexShrink: 0 }} />
                      <div style={{ fontSize: 12, color: "rgba(217,227,220,.85)", lineHeight: 1.65 }}>
                        Review your answers before submitting. Staff assess the quality of your roleplay
                        understanding and character concept — there are no trick questions.
                      </div>
                    </div>

                    <div className="mcap-two" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
                      {[
                        ["Minecraft", form.minecraftUsername],
                        ["Discord", form.discordUsername],
                        ["Character", form.characterName],
                        ["Character age", form.characterAge],
                        ["Timezone", form.timezone],
                        ["Microphone", form.microphone],
                      ].map(([k, v]) => (
                        <div key={k} style={{ padding: "11px 13px", borderRadius: 9, background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.07)" }}>
                          <div style={{ fontSize: 9.5, color: "rgba(178,196,184,.6)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>{k}</div>
                          <div style={{ fontSize: 12.5, color: "#edf2ee", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v || "—"}</div>
                        </div>
                      ))}
                    </div>

                    <label style={{
                      display: "flex", gap: 11, alignItems: "flex-start", padding: "14px 15px", cursor: "pointer",
                      borderRadius: 10, border: `1px solid ${errors.agreements && !form.rulesAccepted ? "rgba(248,113,113,.45)" : "rgba(255,255,255,.09)"}`,
                    }}>
                      <input type="checkbox" checked={form.rulesAccepted} onChange={(e) => setField("rulesAccepted", e.target.checked)} style={{ marginTop: 2, accentColor: "#4ade80" }} />
                      <span style={{ fontSize: 12, color: "rgba(217,227,220,.85)", lineHeight: 1.6 }}>I have read the server rules and understand that being whitelisted does not exempt me from them.</span>
                    </label>
                    <label style={{
                      display: "flex", gap: 11, alignItems: "flex-start", padding: "14px 15px", cursor: "pointer",
                      borderRadius: 10, border: `1px solid ${errors.agreements && !form.truthfulAccepted ? "rgba(248,113,113,.45)" : "rgba(255,255,255,.09)"}`,
                    }}>
                      <input type="checkbox" checked={form.truthfulAccepted} onChange={(e) => setField("truthfulAccepted", e.target.checked)} style={{ marginTop: 2, accentColor: "#4ade80" }} />
                      <span style={{ fontSize: 12, color: "rgba(217,227,220,.85)", lineHeight: 1.6 }}>I confirm the information in this application is my own and accurate to the best of my knowledge.</span>
                    </label>

                    {errors.agreements && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#f87171" }}>
                        <AlertCircle style={{ width: 13, height: 13 }} />Please accept both declarations before submitting.
                      </div>
                    )}
                    {submitState === "unavailable" && (
                      <div style={{ padding: "12px 14px", borderRadius: 9, background: "rgba(248,113,113,.06)", border: "1px solid rgba(248,113,113,.2)", color: "#fca5a5", fontSize: 11.5, lineHeight: 1.6 }}>
                        The application service isn't reachable right now. Your answers are saved on this device — please try submitting again shortly.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 30, paddingTop: 22, borderTop: "1px solid rgba(255,255,255,.07)" }}>
                <button
                  type="button" onClick={goBackStep} disabled={step === 0}
                  className="toa-btn toa-btn-ghost"
                  style={{ opacity: step === 0 ? .35 : 1, display: "flex", alignItems: "center", gap: 6 }}
                >
                  <ChevronLeft style={{ width: 13, height: 13 }} />Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button type="button" onClick={goNext} className="toa-btn toa-btn-p" style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    Continue<ChevronRight style={{ width: 14, height: 14 }} />
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={submitting} className="toa-btn toa-btn-p" style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <Send style={{ width: 13, height: 13 }} />{submitting ? "Submitting..." : "Submit Application"}
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 20, fontSize: 11.5, color: "rgba(178,196,184,.5)" }}>
              <Mic style={{ width: 12, height: 12 }} />Answers are saved automatically as you go, so it's safe to come back later.
            </div>
          </>
        ))}
      </div>
    </div>
  );
}